import React from 'react';

import { Alert, AlertTitle, Box, Typography } from '@mui/material';

import type { CppInvoiceNote } from '~/services/LuxLoyalty';

import { describeInvoiceNote } from './invoiceNotes';

interface Props {
  notes: Array<CppInvoiceNote>;
  severity: 'error' | 'warning';
  title: string;
  /** Invoice currency, for notes whose detail carries an amount. */
  currency?: string | null;
}

/**
 * Renders the service's structured `blockers` or `warnings` as readable copy.
 *
 * Blockers are not cosmetic: while any are present the service refuses to persist costs and sets
 * `invoiceable` to false, so the period's figures are provisional and the invoice must not be
 * saved. Each note's `detail` payload is rendered beneath its headline so an admin can see what to
 * fix — which day is missing a rate, which refund cannot be matched — without leaving the page.
 */
export default function InvoiceNotesAlert({ notes, severity, title, currency = null }: Props) {
  if (notes.length === 0) {
    return null;
  }

  return (
    <Alert severity={severity}>
      <AlertTitle>{title}</AlertTitle>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {notes.map((note, index) => {
          const { headline, detail } = describeInvoiceNote(note, currency);

          return (
            <li key={`${note.code}-${note.date ?? index}`}>
              <Typography variant="body2">{headline}</Typography>
              {detail && (
                <Typography variant="caption" color="text.secondary">
                  {detail}
                </Typography>
              )}
            </li>
          );
        })}
      </Box>
    </Alert>
  );
}
